import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import SelectBox from '@adsk/alloy-select-box';
import FormField from '@adsk/alloy-react-form-field';
import { useLocale } from '@adsk/alloy-react-helpers';
import addCompanyFooterRenderer from '@common/CompanyFieldFooter';
import { fieldStyle } from './styles';
import { COMPONENT_VARIANTS } from '../../../constants';

const EditableMemberInfo = ({
  canCreateCompanies,
  companies,
  companySearchInput,
  companyCreateError,
  roles,
  isFetchingCompanies,
  isFetchingRoles,
  onChangeCompanyInput,
  onChangeRoleInput,
  onCompanyChange,
  onCreateCompany,
  onRoleChange,
  roleSearchInput,
  selectedCompany,
  selectedRole,
  variant,
}) => {
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));
  const fieldContainerRef = useRef();
  const [selectBoxStyles, setSelectBoxStyles] = useState({});

  useEffect(() => {
    setSelectBoxStyles({
      maxWidth: `${fieldContainerRef.current.offsetWidth}px`,
    });
  }, []);

  const companyCreationValidator = () => {
    return canCreateCompanies;
  };

  return (
    <>
      <div css={fieldStyle} ref={fieldContainerRef}>
        <FormField
          error={companyCreateError ? T.translate('ADD_COMPANY_ERROR') : ''}
          label={T.translate('COMPANY_LABEL')}
          labelVariant={FormField.LABEL_VARIANTS.TOP}
        >
          <SelectBox
            clearable
            data-testid="Company_SelectBox"
            footerRenderer={addCompanyFooterRenderer({
              onCreateCompany,
              companyCreationValidator,
            })}
            ignoreCase
            inputValue={companySearchInput}
            invalid={companyCreateError}
            isLoading={isFetchingCompanies}
            labelKey="name"
            maxMenuHeight={150}
            menuStyle={selectBoxStyles}
            onChange={onCompanyChange}
            onInputChange={onChangeCompanyInput}
            options={companies}
            placeholder={T.translate('COMPANY_PLACEHOLDER')}
            searchable
            value={selectedCompany}
            valueKey="id"
          />
        </FormField>
      </div>
      <div css={fieldStyle}>
        <FormField
          label={T.translate('ROLE_LABEL')}
          labelVariant={FormField.LABEL_VARIANTS.TOP}
        >
          <SelectBox
            clearable
            data-testid="Role_SelectBox"
            ignoreCase
            isLoading={isFetchingRoles}
            labelKey="name"
            maxMenuHeight={150}
            menuStyle={selectBoxStyles}
            multiple={variant === COMPONENT_VARIANTS.PROJECT}
            onChange={onRoleChange}
            onInputChange={onChangeRoleInput}
            options={roles.filter((role) => role.status === 'active')}
            placeholder={T.translate('ROLE_PLACEHOLDER')}
            inputValue={roleSearchInput}
            searchable
            value={selectedRole}
            valueKey="id"
          />
        </FormField>
      </div>
    </>
  );
};

EditableMemberInfo.propTypes = {
  canCreateCompanies: PropTypes.bool.isRequired,
  companies: PropTypes.arrayOf(PropTypes.object),
  companyCreateError: PropTypes.bool.isRequired,
  companySearchInput: PropTypes.string,
  isFetchingCompanies: PropTypes.bool.isRequired,
  isFetchingRoles: PropTypes.bool.isRequired,
  onChangeCompanyInput: PropTypes.func,
  onChangeRoleInput: PropTypes.func,
  onCompanyChange: PropTypes.func.isRequired,
  onCreateCompany: PropTypes.func,
  onRoleChange: PropTypes.func.isRequired,
  roles: PropTypes.arrayOf(PropTypes.object),
  roleSearchInput: PropTypes.string,
  selectedCompany: PropTypes.objectOf(PropTypes.string),
  selectedRole: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  variant: PropTypes.oneOf(Object.values(COMPONENT_VARIANTS)).isRequired,
};

EditableMemberInfo.defaultProps = {
  companies: [],
  companySearchInput: undefined,
  onChangeCompanyInput: undefined,
  onChangeRoleInput: undefined,
  onCreateCompany: undefined,
  roles: [],
  roleSearchInput: undefined,
  selectedCompany: null,
  selectedRole: null,
};

export default EditableMemberInfo;
